import { useState } from "react";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Glyph, Modal } from "../../components/ui";
import { Button, Tag } from "../../components/ui/ds";
import { LIFE_CAT } from "../../lib/constants";
import { joinedLabel } from "../../lib/format";

// In-app „launch" modal novej globálnej dávky (napr. „Zrkadlová dvojtýždňovka") — ukáže sa RAZ,
// keď je dávka odoslaná e-mailom (s.global_launch z /api/state) a user ešte nezareagoval. Stavaný
// na zdieľanom Modal-e (rovnako ako ChallengeInfoModal), plne tématizovaný (žiadne surové farby):
// flame ikona v hlavičke, zrkadlová ilustrácia v tóne značky, „Deň X zo Y" a KAŽDÁ výzva má vlastné
// tlačidlo „Zapojiť sa". Zavretie rieši rodič (dismiss + toast „nájdeš v Priatelia → Zapoj sa").

// **tučné** v intro texte → <strong>
function renderIntro(text) {
  return (text || "").split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

// prvé písmeno veľké (intro sa niekedy uloží s malým začiatkom)
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Zrkadlo v tóne značky (bez hnedej/zlatej) — odvodené z --brand-primary derivácií. viewBox má hore
// dosť miesta, aby sa horný okraj rámu neorezal.
function MirrorArt() {
  return (
    <div style={{ textAlign: "center", margin: "6px 0 12px" }}>
      <svg width="104" height="140" viewBox="0 0 104 140" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="bzMirGlass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--brand-primary-surface-strong)" />
            <stop offset="1" stopColor="var(--brand-primary-surface)" />
          </linearGradient>
        </defs>
        <rect x="48" y="96" width="8" height="30" rx="3" fill="var(--brand-primary)" />
        <ellipse cx="52" cy="130" rx="18" ry="5" fill="var(--brand-primary)" opacity="0.45" />
        <ellipse cx="52" cy="52" rx="40" ry="46" fill="none" stroke="var(--brand-primary)" strokeWidth="6" />
        <ellipse cx="52" cy="52" rx="34" ry="40" fill="url(#bzMirGlass)" />
        <path d="M40 40 q10 -8 22 -2" stroke="var(--neutral-surface)" strokeWidth="4" strokeLinecap="round" opacity="0.7" fill="none" />
      </svg>
    </div>
  );
}

export function GlobalLaunchModal({ launch, onClose, onJoin }) {
  const chs = launch.challenges || [];
  // per-výzva busy stav — inak by sa „…" ukázalo na OBOCH tlačidlách (zdieľaný joinBusy)
  const [busyCid, setBusyCid] = useState(null);
  return (
    <Modal center
      iconNode={<BuzerIcon name="flame" size={20} color="var(--brand-primary)" />}
      title={launch.title}
      subtitle={<span style={{ fontFamily: "var(--ff-ui)", textTransform: "none", letterSpacing: "normal", fontSize: 12.5, fontWeight: 600 }}>Nová dvojtýždňová výzva</span>}
      onClose={onClose}
      footer={<Button variant="ghost" block onClick={onClose}>Zavrieť</Button>}>
      {launch.intro && (
        <div className="bz-aitext" style={{ margin: "0 0 4px" }}>
          {renderIntro(cap(launch.intro))}
        </div>
      )}
      <MirrorArt />
      <div style={{ fontFamily: "var(--ff-ui)", fontSize: 12.5, fontWeight: 700, color: "var(--neutral-text-muted)", textAlign: "center", margin: "0 0 12px" }}>
        Deň {launch.day} zo {launch.total} · nech ti séria rastie
      </div>
      {chs.map((c) => {
        const lc = c.life_cat ? LIFE_CAT[c.life_cat] : null;
        return (
          <div key={c.cid} style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "var(--neutral-surface-2)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-lg)", padding: "12px 13px", marginTop: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: "var(--radius-md)", background: "var(--neutral-surface)", border: "1px solid var(--neutral-border-strong)", flexShrink: 0 }}>
              <Glyph icon={c.icon} size={18} fallback="challenge" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--neutral-text-main)", lineHeight: 1.3 }}>{c.name}</div>
              <div style={{ fontSize: 13, color: "var(--neutral-text-muted)", lineHeight: 1.45, marginTop: 2 }}>{c.descr}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                <Tag tone="brand" size="xs" bare>+{c.points}</Tag>
                <Tag tone="neutral" size="xs">{c.is_competition ? "súťaž" : "denne"}</Tag>
                {lc && <Tag tone="neutral" size="xs" icon={lc.icon}>{lc.label}</Tag>}
              </div>
              <div style={{ marginTop: 10 }}>
                {c.joined ? (
                  <Button variant="ghost" size="sm" disabled style={{ color: "var(--brand-primary)" }}>
                    <Icon name="check" size={15} color="currentColor" stroke={2.6} />{joinedLabel()}
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" disabled={busyCid === c.cid}
                    onClick={async () => { setBusyCid(c.cid); try { await onJoin(c.cid); } finally { setBusyCid(null); } }}>
                    {busyCid === c.cid ? "…" : "Zapojiť sa"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </Modal>
  );
}
